import * as React from 'react';

import {
    Box,
    Button,
    CircularProgress,
    Divider,
    FormControlLabel,
    Menu,
    MenuItem,
    RadioGroup,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import {
    createInvoiceIcon,
    createQuoteIcon,
    FaOptionIcon,
    todoIcon,
} from '@europrocurement/l2d-icons';
import { FlexyButton, PriceLigne, Radio, VerticalDivider } from '@europrocurement/flexy-components';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { Container, SxProps, useMediaQuery } from '@mui/system';
import { faEllipsis } from '@fortawesome/sharp-regular-svg-icons';
import {
    Acompte,
    Collection,
    DataPrefacturation,
    useCollectionBillingCtx,
} from '@europrocurement/l2d-modules/providers';
import useDossierFacturationActions from '../../hooks/useDossierFacturationActions';
import {
    facturationAgregee,
    facturationAgregeeDescription,
    facturationAgregeeLibelle,
    facturationComplete,
    facturationCompleteDescription,
    facturationCompleteLibelle,
    facturationSimplifiee,
    facturationSimplifieeDescription,
    facturationSimplifieeLibelle,
    facturationType,
    heightFooterFacturation,
} from '../../functions';

type TotalsSectionProps = {
    totalAcomptes: number;
    acomptes: Acompte[];
    collections: Collection[];
};

const TotalsSection: React.FunctionComponent<TotalsSectionProps> = function ({
    totalAcomptes,
    acomptes,
    collections,
}: TotalsSectionProps) {
    const { enqueueSnackbar } = useSnackbar();
    const { palette } = useTheme();
    const PriceLigneSx: SxProps = { gap: '15px' };
    const isLg = useMediaQuery('(min-width:1024px)');
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const [disabled, setDisabled] = React.useState<boolean>(false);
    const handleClickContextualMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const { ajustInvoice, createEstimates, updateFacturationType, createInvoice, createProforma } =
        useDossierFacturationActions();

    const {
        getDataPrefacturation,
        getBillingDossierIds,
        isFetchingInvoices,
        isFetchingEstimates,
        isFetchingDataPrefacturation,
    } = useCollectionBillingCtx();
    const rawDataPrefacturation = getDataPrefacturation();
    const billingDossierIds = getBillingDossierIds();
    const dataPrefacturation = rawDataPrefacturation
        ? {
              ...rawDataPrefacturation,
              dossiers: rawDataPrefacturation.dossiers.filter(
                  (d) => d.id !== undefined && billingDossierIds.includes(d.id),
              ),
          }
        : null;

    const totalBillable = Number(dataPrefacturation?.ttc);
    const billable = totalBillable > 0 && dataPrefacturation?.facturable;
    const billableDiff = Math.abs((totalBillable || 0) - totalAcomptes);

    const collectionTypeFacturation = collections[0]?.typeFacturation || null;
    const [typeFacturation, setTypeFacturation] = React.useState<facturationType>(
        collectionTypeFacturation ?? facturationComplete,
    );

    const isUpdating =
        isFetchingInvoices() || isFetchingEstimates() || isFetchingDataPrefacturation() || disabled;

    React.useEffect(() => {
        setTypeFacturation(collectionTypeFacturation ?? facturationComplete);
    }, [collectionTypeFacturation]);

    const handleFacturationTypeChange = (newValue: facturationType) => {
        setTypeFacturation(newValue);
        updateFacturationType(collections[0], newValue);
    };

    const handleCreateDevis = async () => {
        setDisabled(true);

        try {
            await createEstimates(collections);
            enqueueSnackbar('Le devis a été créé.');
        } catch (error) {
            if (
                error instanceof AxiosError &&
                error.response &&
                error.response.data.exception &&
                error.response.data.exception.message
            ) {
                enqueueSnackbar(<Typography>{error.response.data.exception.message}</Typography>, {
                    variant: 'error',
                });
            } else if (error instanceof Error) {
                enqueueSnackbar(<Typography>{error.message}</Typography>, {
                    variant: 'error',
                });
            }

            return;
        } finally {
            setDisabled(false);
            handleClose();
        }

        const devisEtFacturesFormElement = document.getElementById('devisEtFactures');
        if (devisEtFacturesFormElement) {
            devisEtFacturesFormElement.scrollIntoView();
        }
    };

    const handleCreateInvoice = async () => {
        setDisabled(true);

        try {
            await createInvoice(dataPrefacturation as DataPrefacturation, acomptes, collections);
            enqueueSnackbar('La facture a été créée.');
        } catch (error) {
            if (
                error instanceof AxiosError &&
                error.response &&
                error.response.data.exception &&
                error.response.data.exception.message
            ) {
                enqueueSnackbar(<Typography>{error.response.data.exception.message}</Typography>, {
                    variant: 'error',
                });
            } else if (error instanceof Error) {
                enqueueSnackbar(<Typography>{error.message}</Typography>, {
                    variant: 'error',
                });
            }

            return;
        } finally {
            setDisabled(false);
            handleClose();
        }

        const devisEtFacturesFormElement = document.getElementById('devisEtFactures');
        if (devisEtFacturesFormElement) {
            devisEtFacturesFormElement.scrollIntoView();
        }
    };

    const handleCreateProforma = async () => {
        setDisabled(true);

        try {
            await createProforma(dataPrefacturation as DataPrefacturation, acomptes, collections);
            enqueueSnackbar('Le proforma a été créé.');
        } catch (error) {
            if (
                error instanceof AxiosError &&
                error.response &&
                error.response.data.exception &&
                error.response.data.exception.message
            ) {
                enqueueSnackbar(<Typography>{error.response.data.exception.message}</Typography>, {
                    variant: 'error',
                });
            } else if (error instanceof Error) {
                enqueueSnackbar(<Typography>{error.message}</Typography>, {
                    variant: 'error',
                });
            }

            return;
        } finally {
            setDisabled(false);
            handleClose();
        }

        const devisEtFacturesFormElement = document.getElementById('devisEtFactures');
        if (devisEtFacturesFormElement) {
            devisEtFacturesFormElement.scrollIntoView();
        }
    };

    const handleAdjustPrice = async () => {
        setDisabled(true);
        try {
            await ajustInvoice(dataPrefacturation as DataPrefacturation, acomptes, collections);
        } catch (error) {
            if (
                error instanceof AxiosError &&
                error.response &&
                error.response.data.exception &&
                error.response.data.exception.message
            ) {
                enqueueSnackbar(<Typography>{error.response.data.exception.message}</Typography>, {
                    variant: 'error',
                });
            } else if (error instanceof Error) {
                enqueueSnackbar(<Typography>{error.message}</Typography>, {
                    variant: 'error',
                });
            }

            return;
        } finally {
            setDisabled(false);
        }
    };

    const typesFacturation = [
        {
            label: facturationCompleteLibelle,
            value: facturationComplete,
            description: facturationCompleteDescription,
        },
        {
            label: facturationSimplifieeLibelle,
            value: facturationSimplifiee,
            description: facturationSimplifieeDescription,
        },
        {
            label: facturationAgregeeLibelle,
            value: facturationAgregee,
            description: facturationAgregeeDescription,
        },
    ];

    const proformaTotalSx = {
        width: '100%',
        fontSize: '13px',
        fontWeight: '500',
        lineHeight: '15.6px',
        color: palette.grey[500],
    };

    if (!dataPrefacturation) {
        return null;
    }

    return (
        <>
            <Box
                sx={{
                    background: palette.background.paper,
                    position: 'fixed',
                    boxShadow: '1px 1px 15px 0px rgba(0, 0, 0, 0.2)',
                    bottom: '0px',
                    left: '0px',
                    width: '100%',
                    height: heightFooterFacturation,
                }}
            />
            <Container
                sx={{
                    position: 'fixed',
                    bottom: '0px',
                    display: 'flex',
                    flexDirection: {
                        xs: 'column',
                        sm: 'column',
                        md: 'row',
                        lg: 'row',
                        xl: 'row',
                    },
                    height: heightFooterFacturation,
                    justifyContent: 'space-around',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '15px',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Box>
                        <PriceLigne
                            label="Reste à facturer : "
                            sx={PriceLigneSx}
                            price={isUpdating ? '- €' : `${dataPrefacturation.resteAFacturer} €`}
                        />
                        {dataPrefacturation.ttcProforma &&
                        Number(dataPrefacturation.ttcProforma) > 0 ? (
                            <Typography sx={proformaTotalSx}>
                                {isUpdating
                                    ? 'Dont - € en proforma'
                                    : `Dont ${dataPrefacturation.ttcProforma} € en proforma`}
                            </Typography>
                        ) : null}
                    </Box>
                    <Box>
                        <PriceLigne
                            labelTypographyProps={{ fontWeight: '600' }}
                            priceTypographyProps={{ fontWeight: '600' }}
                            label="Provision non affectée : "
                            sx={PriceLigneSx}
                            price={isUpdating ? '- €' : `${totalAcomptes} €`}
                        />
                        <Divider />
                        <PriceLigne
                            labelTypographyProps={{ fontWeight: '600' }}
                            priceTypographyProps={{ fontSize: '22px', fontWeight: '600' }}
                            label="Facturable : "
                            sx={PriceLigneSx}
                            price={isUpdating ? '- €' : `${totalBillable?.toFixed(2)} €`}
                        />
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        flexDirection: {
                            xs: 'row',
                            sm: 'row',
                            md: 'column',
                            lg: 'column',
                            xl: 'column',
                        },
                    }}
                >
                    <Box
                        display="flex"
                        alignItems="center"
                    >
                        {typeFacturation && (
                            <RadioGroup
                                sx={{ flexGrow: '1' }}
                                aria-labelledby="invoice-type-radio-buttons-group-label"
                                name="invoice-type-buttons-group"
                                value={typeFacturation}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    const newValue = event.target.value as facturationType;
                                    handleFacturationTypeChange(newValue);
                                }}
                            >
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                >
                                    {typesFacturation.map(({ value, label, description }) => (
                                        <Tooltip title={description}>
                                            <FormControlLabel
                                                value={value}
                                                control={<Radio />}
                                                label={label}
                                            />
                                        </Tooltip>
                                    ))}
                                </Box>
                            </RadioGroup>
                        )}
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexDirection: 'row',
                            gap: '5px',
                            minHeight: '50px',
                        }}
                    >
                        {isUpdating && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    flexDirection: 'row',
                                    columnGap: '1em',
                                }}
                            >
                                <CircularProgress
                                    size="16px"
                                    sx={{ width: '16px', height: '16px' }}
                                />
                                <Typography>Mise à jour en cours</Typography>
                            </Box>
                        )}
                        {!isUpdating && (
                            <>
                                <FlexyButton
                                    variant="text"
                                    disabled={
                                        !(
                                            billableDiff <= 15 &&
                                            billableDiff > 0 &&
                                            totalAcomptes > 0
                                        ) ||
                                        !billable ||
                                        disabled
                                    }
                                    onClick={handleAdjustPrice}
                                    sx={{ float: 'right', fontWeight: '500' }}
                                    icon={todoIcon}
                                    label={isLg ? 'Ajuster le prix' : 'Ajuster'}
                                />
                                <Tooltip
                                    title={!billable ? dataPrefacturation.motifNonFacturable : null}
                                >
                                    <Box>
                                        <FlexyButton
                                            disabled={!billable || disabled}
                                            variant="contained"
                                            onClick={handleCreateInvoice}
                                            icon={createInvoiceIcon}
                                            label={isLg ? 'Créer la facture' : 'Facture'}
                                        />
                                    </Box>
                                </Tooltip>

                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <VerticalDivider sx={{ margin: '0px 10px' }} />

                                    <Button
                                        id="toolbar-contextual-menu-btn"
                                        aria-controls={open ? 'toolbar-contextual-menu' : undefined}
                                        aria-haspopup="true"
                                        aria-expanded={open ? 'true' : undefined}
                                        onClick={handleClickContextualMenu}
                                        sx={{ color: 'text.gris70' }}
                                    >
                                        <Box>
                                            <FaOptionIcon
                                                icon={faEllipsis}
                                                size="lg"
                                            />
                                        </Box>
                                    </Button>
                                    <Menu
                                        id="toolbar-contextual-menu"
                                        aria-labelledby="toolbar-contextual-menu"
                                        anchorEl={anchorEl}
                                        open={open}
                                        onClose={handleClose}
                                        MenuListProps={{ sx: { py: 0 } }}
                                    >
                                        <MenuItem
                                            disabled={disabled}
                                            onClick={handleCreateDevis}
                                            sx={{ gap: '4px' }}
                                        >
                                            <FaOptionIcon
                                                {...createQuoteIcon.props}
                                                color="text.gris70"
                                            />
                                            <Typography sx={{ color: 'text.primary' }}>
                                                Créer le devis
                                            </Typography>
                                        </MenuItem>
                                        {billable ? (
                                            <MenuItem
                                                disabled={disabled}
                                                onClick={handleCreateProforma}
                                                sx={{ gap: '4px' }}
                                            >
                                                <FaOptionIcon
                                                    {...createInvoiceIcon.props}
                                                    color="text.gris70"
                                                />
                                                <Typography sx={{ color: 'text.primary' }}>
                                                    Créer le proforma
                                                </Typography>
                                            </MenuItem>
                                        ) : null}
                                    </Menu>
                                </Box>
                            </>
                        )}
                    </Box>
                </Box>
            </Container>
        </>
    );
};

export default TotalsSection;
